import { useEffect, useState, useMemo } from "react";
import { api } from "@/App";
import { toast } from "sonner";
import { Users, Send, Power, Trash2, Eye, RefreshCw, CheckCircle2, AlertCircle, X, Mail, Server, Sparkles } from "lucide-react";

function PreviewModal({ user, onClose, templateHtml }) {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.post("/preview", { template_html: templateHtml, user_id: user.id })
      .then(r => setHtml(r.data.html))
      .catch(e => toast.error("Preview failed: " + (e.response?.data?.detail || e.message)))
      .finally(() => setLoading(false));
  }, [user.id, templateHtml]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-[#0a0e1c] border border-white/10 rounded-xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          <div>
            <h3 className="font-semibold">Preview — {user.displayName}</h3>
            <div className="text-xs text-slate-400">{user.mail || user.userPrincipalName}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white" data-testid="close-preview">
            <X size={20} />
          </button>
        </div>
        <div className="p-6 overflow-auto">
          {loading ? (
            <div className="text-slate-400">Rendering…</div>
          ) : (
            <div className="preview-shell" data-testid="preview-html" dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </div>
      </div>
    </div>
  );
}

function ModeBadge({ active, children, testId }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all ${active ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`} data-testid={testId}>
      {children}
    </div>
  );
}

const MODES = {
  owa: {
    key: "owa",
    label: "Outlook Signature",
    desc: "Appears in compose & replies — syncs to iPhone",
    Icon: Mail,
  },
  rule: {
    key: "rule",
    label: "Server Rule",
    desc: "Appended at send-time (always at bottom)",
    Icon: Server,
  },
};

export default function Dashboard() {
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("owa"); // "owa" or "rule"

  const refresh = async () => {
    setLoading(true);
    try {
      const [u, t, s] = await Promise.all([
        api.get("/users"),
        api.get("/templates"),
        api.get("/settings"),
      ]);
      setUsers(u.data);
      setTemplates(t.data);
      setSettings(s.data);
    } catch (e) {
      toast.error("Failed to load: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const activeTemplate = useMemo(
    () => templates.find(t => t.id === settings?.activeTemplateId),
    [templates, settings]
  );

  const hasAnyRules = users.some(u => u.deployed);

  const toggleSelect = (id) => {
    const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n);
  };
  const toggleAll = () => {
    if (selected.size === users.length) setSelected(new Set());
    else setSelected(new Set(users.map(u => u.id)));
  };

  const deploy = async (userIds) => {
    if (!activeTemplate) { toast.error("Set an active template in Brand Settings first."); return; }
    setBusy(true);
    try {
      const endpoint = mode === "owa" ? "/deploy/owa" : "/deploy";
      const r = await api.post(endpoint, { user_ids: userIds });
      const modeLabel = mode === "owa" ? "Outlook signature" : "transport rule";
      toast.success(`${modeLabel}: ${r.data.success}/${r.data.total} mailboxes`);
      const failures = r.data.results.filter(x => x.ok === false);
      failures.forEach(f => toast.error(`${f.mail}: ${f.error}`, { duration: 8000 }));
      await refresh();
      setSelected(new Set());
    } catch (e) {
      toast.error("Deploy failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const cleanupAllRules = async () => {
    if (!window.confirm("Remove ALL server transport rules created by this app? Outlook signatures are not affected.")) return;
    setBusy(true);
    try {
      const r = await api.post("/deploy/rules/cleanup");
      toast.success(`Removed ${r.data.removed} of ${r.data.total} transport rules`);
      await refresh();
    } catch (e) {
      toast.error("Cleanup failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const removeUserDeployment = async (u) => {
    if (mode === "owa") {
      if (!u.owa?.hasHtml) { toast.info("No Outlook signature to remove"); return; }
      if (!window.confirm(`Clear Outlook signature for ${u.mail || u.userPrincipalName}?`)) return;
      try {
        await api.delete(`/deploy/owa/${u.id}`);
        toast.success("Outlook signature cleared");
        refresh();
      } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    } else {
      if (!u.deployed) { toast.info("No transport rule to remove"); return; }
      if (!window.confirm(`Remove transport rule for ${u.mail || u.userPrincipalName}?`)) return;
      try {
        await api.delete(`/deploy/${u.id}`);
        toast.success("Rule removed");
        refresh();
      } catch (e) { toast.error("Failed: " + (e.response?.data?.detail || e.message)); }
    }
  };

  return (
    <div className="p-8 max-w-7xl">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-emerald-400 text-xs uppercase tracking-widest mb-1">
            <Users size={14} /> Mailboxes
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Users & Deployment</h1>
          <p className="text-slate-400 mt-1 text-sm max-w-3xl">
            Pick a deployment mode below, then deploy. Outlook Signature mode is recommended — the signature appears in your compose window and on replies (above the quoted thread), and syncs to iPhone automatically.
          </p>
        </div>
        <button onClick={refresh} className="btn" data-testid="refresh-btn" disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Mode picker */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
        {Object.values(MODES).map(m => {
          const { Icon } = m;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => setMode(m.key)}
              data-testid={`mode-${m.key}`}
              className={`text-left p-4 rounded-xl border transition-all ${mode === m.key ? "border-emerald-500/40 bg-emerald-500/10 ring-1 ring-emerald-500/30" : "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]"}`}
            >
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${mode === m.key ? "bg-emerald-500/20 text-emerald-300" : "bg-white/5 text-slate-300"}`}>
                  <Icon size={18} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-semibold text-sm">{m.label}</div>
                    {m.key === "owa" && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 uppercase tracking-wider">Recommended</span>}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">{m.desc}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Cleanup banner when switching to OWA but rules exist */}
      {mode === "owa" && hasAnyRules && (
        <div className="glass p-4 mb-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <Sparkles className="text-amber-400 shrink-0 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-medium text-amber-200">You have old server transport rules from the previous mode</div>
            <div className="text-xs text-slate-400 mt-0.5">These cause the signature to appear at the bottom of full reply threads. Recommended: remove them so only the Outlook signature is active.</div>
          </div>
          <button onClick={cleanupAllRules} disabled={busy} className="btn btn-danger shrink-0" data-testid="cleanup-rules-btn">
            <Trash2 size={14} /> Remove all rules
          </button>
        </div>
      )}

      {!activeTemplate && (
        <div className="glass p-4 mb-4 border border-amber-500/30 bg-amber-500/5 flex items-start gap-3">
          <AlertCircle className="text-amber-400 shrink-0 mt-0.5" size={18} />
          <div>
            <div className="font-medium text-amber-200">No active template selected</div>
            <div className="text-xs text-slate-400 mt-0.5">Pick one in <strong>Brand Settings</strong> before deploying.</div>
          </div>
        </div>
      )}

      {activeTemplate && (
        <div className="glass p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="text-emerald-400" size={18} />
            <div>
              <div className="text-sm">Active template: <span className="font-semibold text-emerald-300">{activeTemplate.name}</span></div>
              <div className="text-xs text-slate-400">Mode: <strong>{MODES[mode].label}</strong> — {MODES[mode].desc}</div>
            </div>
          </div>
          <button
            onClick={() => deploy(null)}
            disabled={busy || users.length === 0}
            className="btn btn-primary"
            data-testid="deploy-all-btn"
          >
            <Send size={14} /> {busy ? "Working…" : `Deploy to all (${users.length})`}
          </button>
        </div>
      )}

      <div className="glass overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/5 bg-white/[0.02] flex items-center justify-between">
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={users.length > 0 && selected.size === users.length} onChange={toggleAll} className="accent-emerald-500" data-testid="select-all" />
            {selected.size > 0 ? `${selected.size} selected` : `${users.length} mailboxes`}
          </label>
          {selected.size > 0 && activeTemplate && (
            <button onClick={() => deploy(Array.from(selected))} disabled={busy} className="btn btn-primary" data-testid="deploy-selected-btn">
              <Send size={13} /> Deploy {selected.size}
            </button>
          )}
        </div>

        {loading && users.length === 0 ? (
          <div className="p-8 text-center text-slate-500">Loading users from Microsoft 365…</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400 border-b border-white/5">
              <tr>
                <th className="text-left px-4 py-2 w-8" />
                <th className="text-left px-4 py-2">Name</th>
                <th className="text-left px-4 py-2">Email</th>
                <th className="text-left px-4 py-2"><span className="inline-flex items-center gap-1"><Mail size={11} /> Outlook Sig</span></th>
                <th className="text-left px-4 py-2"><span className="inline-flex items-center gap-1"><Server size={11} /> Server Rule</span></th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => {
                const owaActive = u.owa?.hasHtml && (u.owa.auto || u.owa.onReply);
                const ruleActive = u.deployed && u.deployed.state === "Enabled";
                return (
                  <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} className="accent-emerald-500" data-testid={`select-${u.id}`} />
                    </td>
                    <td className="px-4 py-3"><div className="font-medium">{u.displayName}</div></td>
                    <td className="px-4 py-3 text-slate-300">{u.mail || u.userPrincipalName}</td>
                    <td className="px-4 py-3">
                      {u.owa === null ? (
                        <span className="text-xs text-slate-600">—</span>
                      ) : owaActive ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Active</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 border border-slate-700">Empty</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ruleActive ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Live</span>
                      ) : u.deployed ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">{u.deployed.state}</span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-400 border border-slate-700">Not set</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end items-center gap-1">
                        {activeTemplate && (
                          <button onClick={() => setPreview(u)} className="btn px-2 py-1" data-testid={`preview-${u.id}`} title="Preview"><Eye size={13} /></button>
                        )}
                        {activeTemplate && (
                          <button onClick={() => deploy([u.id])} disabled={busy} className="btn btn-primary px-2 py-1" data-testid={`deploy-${u.id}`} title={`Deploy ${MODES[mode].label}`}><Send size={13} /></button>
                        )}
                        {((mode === "owa" && u.owa?.hasHtml) || (mode === "rule" && u.deployed)) && (
                          <button onClick={() => removeUserDeployment(u)} className="btn btn-danger px-2 py-1" data-testid={`remove-${u.id}`} title="Remove"><Trash2 size={13} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {preview && activeTemplate && (
        <PreviewModal user={preview} templateHtml={activeTemplate.html} onClose={() => setPreview(null)} />
      )}
    </div>
  );
}
